'use client';

import Header from '@/components/Header';

export default function SafetyTips() {
  return (
    <div className="bg-white">
      {/* Header */}
      <Header variant="fixed" />

      {/* Content Section */}
      <section className="pt-32 pb-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold text-gray-900 mb-8">
              Safety Tips for Online Matrimony
            </h1>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 mb-6">
                Your safety and security are our top priorities. These safety tips will help you have a secure and positive experience while using Bhagyavivah.
              </p>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
                Profile Safety
              </h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                Protect Your Personal Information
              </h3>
              <p className="text-gray-600 mb-4">
                When creating your profile, be mindful of what information you share:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 ml-4">
                <li>Never share your home address in your profile</li>
                <li>Avoid posting your exact workplace location</li>
                <li>Don&apos;t include your phone number in profile descriptions</li>
                <li>Use a general area instead of specific landmarks</li>
                <li>Keep financial information completely private</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                Profile Photo Guidelines
              </h3>
              <p className="text-gray-600 mb-4">
                Choose profile photos that are both attractive and safe:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 ml-4">
                <li>Use recent, clear photos that represent you accurately</li>
                <li>Avoid photos that reveal your exact location</li>
                <li>Don&apos;t include photos with car license plates visible</li>
                <li>Ensure photos don&apos;t show your home address or workplace</li>
                <li>Use photos that maintain your privacy and dignity</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
                Communication Safety
              </h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                Initial Conversations
              </h3>
              <p className="text-gray-600 mb-4">
                Take your time getting to know someone before sharing personal details:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 ml-4">
                <li>Start with general conversations about interests and values</li>
                <li>Ask questions to understand their background and intentions</li>
                <li>Be cautious of people who rush into serious commitments</li>
                <li>Trust your instincts - if something feels wrong, it probably is</li>
                <li>Report any suspicious or inappropriate behavior immediately</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                Red Flags to Watch For
              </h3>
              <p className="text-gray-600 mb-4">
                Be aware of these warning signs:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 ml-4">
                <li>Requests for money or financial assistance</li>
                <li>Pressure to meet in person too quickly</li>
                <li>Inconsistent stories or information</li>
                <li>Refusal to video call or meet in public</li>
                <li>Aggressive or manipulative behavior</li>
                <li>Requests for intimate photos or videos</li>
                <li>Claims of emergency situations requiring immediate help</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
                Meeting in Person
              </h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                First Meeting Guidelines
              </h3>
              <p className="text-gray-600 mb-4">
                When you&apos;re ready to meet someone in person:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 ml-4">
                <li>Always meet in a public place for the first few meetings</li>
                <li>Choose busy, well-lit locations like coffee shops or restaurants</li>
                <li>Inform a friend or family member about your meeting plans</li>
                <li>Share the person&apos;s profile information with someone you trust</li>
                <li>Keep your first meeting short (1-2 hours maximum)</li>
                <li>Use your own transportation to and from the meeting</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                Safety During Meetings
              </h3>
              <p className="text-gray-600 mb-4">
                Stay safe during your in-person interactions:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 ml-4">
                <li>Stay in public areas throughout the meeting</li>
                <li>Don&apos;t accept rides from someone you just met</li>
                <li>Keep your personal belongings close to you</li>
                <li>Be cautious about sharing your exact location</li>
                <li>Trust your instincts and leave if you feel uncomfortable</li>
                <li>Have an exit strategy planned in advance</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
                Financial Safety
              </h2>
              <p className="text-gray-600 mb-4">
                Protect yourself from financial scams:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 ml-4">
                <li>Never send money to someone you&apos;ve only met online</li>
                <li>Be suspicious of sob stories requiring financial help</li>
                <li>Don&apos;t share your bank account or credit card details</li>
                <li>Avoid people who ask for loans or investments</li>
                <li>Report any financial requests to our support team</li>
                <li>Remember: genuine relationships don&apos;t require money</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
                Digital Security
              </h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                Account Security
              </h3>
              <p className="text-gray-600 mb-4">
                Keep your Bhagyavivah account secure:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 ml-4">
                <li>Use a strong, unique password for your account</li>
                <li>Enable two-factor authentication if available</li>
                <li>Never share your login credentials with anyone</li>
                <li>Log out from shared or public computers</li>
                <li>Regularly update your password</li>
                <li>Monitor your account for any suspicious activity</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                Online Privacy
              </h3>
              <p className="text-gray-600 mb-4">
                Protect your privacy while using online platforms:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 ml-4">
                <li>Be careful about what you share on social media</li>
                <li>Review your privacy settings regularly</li>
                <li>Don&apos;t connect personal social media accounts too quickly</li>
                <li>Be cautious about sharing your daily routine</li>
                <li>Consider using a separate email for online dating</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
                Trust Your Instincts
              </h2>
              <p className="text-gray-600 mb-4">
                Your intuition is a powerful safety tool:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 ml-4">
                <li>If something feels wrong, trust that feeling</li>
                <li>Don&apos;t ignore red flags or warning signs</li>
                <li>Take your time getting to know someone</li>
                <li>Don&apos;t feel pressured to rush into anything</li>
                <li>It&apos;s okay to say no or end conversations</li>
                <li>Your safety is more important than being polite</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
                Reporting and Support
              </h2>
              <p className="text-gray-600 mb-4">
                We&apos;re here to help keep you safe:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-4 ml-4">
                <li>Report any suspicious or inappropriate behavior immediately</li>
                <li>Contact our support team if you have safety concerns</li>
                <li>Block and report users who make you uncomfortable</li>
                <li>Save evidence of concerning conversations or behavior</li>
                <li>Don&apos;t hesitate to contact local authorities if needed</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
                Emergency Contacts
              </h2>
              <p className="text-gray-600 mb-4">
                Keep these numbers handy:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <p className="text-gray-700">
                  <strong>Bhagyavivah Support:</strong> {process.env.NEXT_PUBLIC_SUPPORT_PHONE_NUMBER || '+91-9326013123'}<br />
                  <strong>Emergency Services:</strong> 100 (Police), 101 (Fire), 102 (Ambulance)<br />
                  <strong>Women Helpline:</strong> 1091<br />
                  <strong>Cyber Crime:</strong> 1930
                </p>
              </div>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
                Remember
              </h2>
              <p className="text-gray-600 mb-4">
                Online matrimony can be a wonderful way to find your life partner, but it&apos;s important to prioritize your safety. Take your time, trust your instincts, and never compromise on your security. We&apos;re committed to providing a safe platform for genuine connections.
              </p>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg mt-8">
                <p className="text-blue-800 font-medium">
                  <strong>Safety First:</strong> If you ever feel unsafe or encounter suspicious behavior, 
                  immediately stop communication and report it to our support team. Your safety is our priority.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
